// src/pages/admin/Utilizadores.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";

import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// ------------------- Filtros por coluna -------------------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

function AtivoFilter({ column: { filterValue, setFilter } }) {
  return (
    <select
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    >
      <option value="">Todos</option>
      <option value="0">Ativos</option>
      <option value="1">Inativos</option>
    </select>
  );
}

// ------------------- Persistência de filtros -------------------
const FILTERS_KEY = "tbl:utilizadores:filters";
const KEEP_KEY = "tbl:utilizadores:keep";

export default function Utilizadores() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [data, setData] = useState([]);

  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;

  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  const carregar = async () => {
    const [payload, err] = await handleApi(api.post("UtilizadoresAcessos/SelUtilizadores", {}));
    if (err) {
      showAlert(err, "error");
      return;
    }
    setData(Array.isArray(payload) ? payload : []);
  };

  useEffect(() => {
    (async () => {
      await carregar();
    })();
  }, []);

  // ---------- ADD ----------
  const [openAdd, setOpenAdd] = useState(false);
  const [novo, setNovo] = useState({
    designacao: "",
    // campos gerais
    refvendedor: "",
    dataadmissao: "", // "YYYY-MM-DD"
    telemovel: "",
    observacoes: "",
    // controlo de utilizador
    eUtilizador: false, // mostra/obriga credenciais apenas se true
    username: "",
    pwd: "",
    email: "@jfbebidas.com",
  });

  // ---------- EDIT ----------
  const [openEdit, setOpenEdit] = useState(false);
  const [sel, setSel] = useState(null);
  const [selIsUser, setSelIsUser] = useState(true);

  // Dialog de confirmação para ativar/inativar
  const [openConfirm, setOpenConfirm] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [novoEstado, setNovoEstado] = useState(null); // 0 ou 1

  const capitalizeWords = (str) =>
    (str ?? "")
      .trim()
      .toLowerCase()
      .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());

  // ------------------- Colunas + Tabela -------------------
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 88,
        minWidth: 72,
        maxWidth: 112,
        Cell: ({ row }) => {
          const u = row.original;
          const [anchor, setAnchor] = useState(null);
          const open = Boolean(anchor);
          const handleOpen = (e) => setAnchor(e.currentTarget);
          const handleClose = () => setAnchor(null);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
              <IconButton
                color="primary"
                size="small"
                disabled={!temPermissao("Menu Administração", "u")}
                onClick={() => {
                  setSel({
                    ...u,
                    pwd: "",
                    // garantir campos
                    refvendedor: u.refvendedor || "",
                    dataadmissao: u.dataadmissao || "",
                    telemovel: u.telemovel || "",
                    observacoes: u.observacoes || "",
                  });
                  setSelIsUser(!!(u?.username || u?.email));
                  setOpenEdit(true);
                }}
                title="Editar"
              >
                <EditIcon fontSize="small" />
              </IconButton>

              <IconButton
                color={u.inativo === 1 ? "success" : "error"}
                size="small"
                disabled={!temPermissao("Menu Administração", "d")}
                onClick={() => {
                  setTargetUser(u);
                  setNovoEstado(u.inativo === 1 ? 0 : 1);
                  setOpenConfirm(true);
                }}
                title={u.inativo === 1 ? "Reativar" : "Inativar"}
              >
                {u.inativo === 1 ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <BlockIcon fontSize="small" />
                )}
              </IconButton>

              <IconButton size="small" onClick={handleOpen} title="Mais ações">
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={anchor} open={open} onClose={handleClose}>
                <MenuItem
                  onClick={() => {
                    handleClose();
                    showAlert("Ver detalhes (em breve)");
                  }}
                >
                  Ver detalhes
                </MenuItem>
              </Menu>
            </div>
          );
        },
      },
      { Header: "Designação", accessor: "designacao", Filter: TextFilter },
      { Header: "Username", accessor: "username", Filter: TextFilter },
      { Header: "Email", accessor: "email", Filter: TextFilter },
      { Header: "Ref. Vendedor", accessor: "refvendedor", Filter: TextFilter },
      {
        Header: "Ativo",
        accessor: "inativo",
        Filter: AtivoFilter,
        Cell: ({ value }) =>
          value === 0 ? (
            <CheckCircleIcon style={{ color: "black" }} fontSize="small" />
          ) : (
            <BlockIcon style={{ color: "black" }} fontSize="small" />
          ),
      },
    ],
    [temPermissao]
  );

  const table = useTable(
    {
      columns,
      data,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  // ---------- Ações ----------
  const inserir = async () => {
    if (!novo.designacao) {
      showAlert("Preenche o Nome.", "warning");
      return;
    }
    if (novo.eUtilizador && (!novo.username || !novo.pwd || !novo.email)) {
      showAlert("Para utilizador da app, preenche Username, Password e Email.", "warning");
      return;
    }

    const body = {
      designacao: novo.designacao,
      username: novo.eUtilizador ? novo.username : null,
      pwd: novo.eUtilizador ? novo.pwd : null,
      email: novo.eUtilizador ? novo.email : null,
      refvendedor: novo.refvendedor || null,
      dataadmissao: novo.dataadmissao || null,
      telemovel: novo.telemovel || null,
      observacoes: novo.observacoes || null,
    };

    const [_, err, meta] = await handleApi(api.post("UtilizadoresAcessos/InsUtilizador", body));
    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");

    await carregar();
    setOpenAdd(false);
    setNovo({
      designacao: "",
      refvendedor: "",
      dataadmissao: "",
      telemovel: "",
      observacoes: "",
      eUtilizador: true,
      username: "",
      pwd: "",
      email: "@jfbebidas.com",
    });
  };

  const gravarEdicao = async () => {
    const body = {
      id: sel.id,
      designacao: sel.designacao,
      username: selIsUser ? sel.username : null,
      pwd: selIsUser ? sel.pwd : null, // se vazio, backend mantém
      email: selIsUser ? sel.email : null,
      refvendedor: sel.refvendedor || null,
      dataadmissao: sel.dataadmissao || null,
      telemovel: sel.telemovel || null,
      observacoes: sel.observacoes || null,
    };

    const [_, err, meta] = await handleApi(api.post("UtilizadoresAcessos/UpdUtilizador", body));
    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    setOpenEdit(false);
  };

  const confirmarEstado = async () => {
    if (!targetUser) return setOpenConfirm(false);
    const [_, err, meta] = await handleApi(
      api.post("UtilizadoresAcessos/AtualizarEstadoUtilizador", {
        id: targetUser.id,
        inativo: novoEstado,
      })
    );
    if (err) showAlert(err, "error");
    else if (meta?.message) showAlert(meta.message, meta.severity || "success");
    await carregar();
    if (!keepFilters) setAllFilters([]);
    setOpenConfirm(false);
    setTargetUser(null);
    setNovoEstado(null);
  };

  // ---------- UI ----------
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                />
                <MDButton
                  color="success"
                  disabled={!temPermissao("Menu Administração", "w")}
                  onClick={() => setOpenAdd(true)}
                >
                  Novo Funcionário
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            width: col.id === "acoes" ? col.width : undefined,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>
                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              padding: "8px 10px",
                              borderTop: "1px solid #eee",
                              fontSize: 12,
                              color: "#1a2b4c",
                              width: cell.column.id === "acoes" ? cell.column.width : undefined,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* Editar */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Utilizador</DialogTitle>
        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <MDInput
              label="Designação"
              value={sel?.designacao || ""}
              onChange={(e) => setSel((p) => ({ ...p, designacao: e.target.value }))}
              onBlur={(e) => setSel((p) => ({ ...p, designacao: capitalizeWords(e.target.value) }))}
              fullWidth
            />

            <MDInput
              label="Ref. Vendedor"
              value={sel?.refvendedor || ""}
              onChange={(e) => setSel((p) => ({ ...p, refvendedor: e.target.value }))}
              fullWidth
            />

            <MDInput
              label="Data de admissão"
              type="date"
              value={sel?.dataadmissao || ""}
              onChange={(e) => setSel((p) => ({ ...p, dataadmissao: e.target.value }))}
              fullWidth
            />

            <MDInput
              label="Telemóvel"
              value={sel?.telemovel || ""}
              onChange={(e) => setSel((p) => ({ ...p, telemovel: e.target.value }))}
              fullWidth
            />

            <MDInput
              label="Observações"
              value={sel?.observacoes || ""}
              onChange={(e) => setSel((p) => ({ ...p, observacoes: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={selIsUser}
                  onChange={(e) => setSelIsUser(e.target.checked)}
                  size="small"
                />
              }
              label="É utilizador da app"
            />

            {selIsUser && (
              <>
                <MDInput
                  label="Username"
                  value={sel?.username || ""}
                  onChange={(e) => setSel((p) => ({ ...p, username: e.target.value }))}
                  onBlur={(e) =>
                    setSel((p) => ({ ...p, username: e.target.value.trim().toLowerCase() }))
                  }
                  fullWidth
                />
                <MDInput
                  label="Password (deixar vazia para manter)"
                  type="password"
                  value={sel?.pwd || ""}
                  onChange={(e) => setSel((p) => ({ ...p, pwd: e.target.value }))}
                  fullWidth
                />
                <MDInput
                  label="Email"
                  type="email"
                  value={sel?.email || ""}
                  onChange={(e) => setSel((p) => ({ ...p, email: e.target.value }))}
                  fullWidth
                />
              </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenEdit(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={gravarEdicao} color="success">
            Guardar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Adicionar */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Novo Utilizador ou Funcionário</DialogTitle>
        <DialogContent component="form" autoComplete="off">
          <MDBox display="grid" gap={1.25} mt={0.5}>
            <MDInput
              label="Nome"
              value={novo.designacao}
              onChange={(e) => setNovo((p) => ({ ...p, designacao: e.target.value }))}
              onBlur={(e) =>
                setNovo((p) => ({ ...p, designacao: capitalizeWords(e.target.value) }))
              }
              fullWidth
            />

            <MDInput
              label="Ref. Vendedor"
              value={novo.refvendedor || ""}
              onChange={(e) => setNovo((p) => ({ ...p, refvendedor: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Data de admissão"
              type="date"
              value={novo.dataadmissao || ""}
              onChange={(e) => setNovo((p) => ({ ...p, dataadmissao: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <MDInput
              label="Telemóvel"
              value={novo.telemovel || ""}
              onChange={(e) => setNovo((p) => ({ ...p, telemovel: e.target.value }))}
              fullWidth
            />

            <MDInput
              label="Observações"
              value={novo.observacoes || ""}
              onChange={(e) => setNovo((p) => ({ ...p, observacoes: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={novo.eUtilizador}
                  onChange={(e) =>
                    setNovo((p) => ({
                      ...p,
                      eUtilizador: e.target.checked,
                      username: e.target.checked ? "" : p.username,
                      pwd: e.target.checked ? "" : p.pwd,
                      email: e.target.checked ? "@jfbebidas.com" : p.email,
                    }))
                  }
                  size="small"
                />
              }
              label="É utilizador da app"
            />

            {novo.eUtilizador && (
              <>
                <MDInput
                  label="Username"
                  value={novo.username}
                  onChange={(e) => setNovo((p) => ({ ...p, username: e.target.value }))}
                  onBlur={(e) =>
                    setNovo((p) => ({ ...p, username: e.target.value.trim().toLowerCase() }))
                  }
                  fullWidth
                  inputProps={{
                    autoComplete: "username",
                    autoCapitalize: "none",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                />
                <MDInput
                  label="Password"
                  type="password"
                  value={novo.pwd}
                  onChange={(e) => setNovo((p) => ({ ...p, pwd: e.target.value }))}
                  fullWidth
                />
                <MDInput
                  label="Email"
                  type="email"
                  value={novo.email}
                  onChange={(e) => setNovo((p) => ({ ...p, email: e.target.value }))}
                  fullWidth
                  inputProps={{
                    autoComplete: "new-password",
                    autoCapitalize: "none",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                />
              </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAdd(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={inserir} color="success">
            Inserir
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* CONFIRMAR ATIVAR/INATIVAR */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {novoEstado === 1 ? "Inativar utilizador" : "Reativar utilizador"}
        </DialogTitle>
        <DialogContent>
          {novoEstado === 1 ? "Inativar" : "Reativar"} o utilizador{" "}
          <strong>{targetUser?.designacao ?? ""}</strong>?
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenConfirm(false)} color="secondary">
            Cancelar
          </MDButton>
          <MDButton onClick={confirmarEstado} color={novoEstado === 1 ? "error" : "success"}>
            {novoEstado === 1 ? "Inativar" : "Reativar"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
